import React from 'react';
import classNames from 'classnames';

import {invariant} from './invariant';

export interface CommonProps {
  /**
   * If provided, will be used to check against accessibility violations.
   *
   * @note In almost all cases `aria-label` should not be used. Proper labelling should be already accounted for within the components.
   */
  'aria-label'?: string;
  /**
   * If provided, the additional class name to provide to the root element.
   *
   * @note This property is prefixed with `UNSAFE` as its use often results in
   * unintended side effects.
   */
  UNSAFE_className?: string;
  /**
   * If provided, the `style` to provide to the root element.
   *
   * @note This property is prefixed with `UNSAFE` as its use often results in
   * unintended side effects.
   */
  UNSAFE_style?: React.CSSProperties;
}

type AppliedCommonProps<T> = T & {
  /**
   * If provided, the additional class name to provide to the root element.
   */
  className?: string;
  /**
   * If provided, the `style` to provide to the root element.
   */
  style?: React.CSSProperties;
};

/**
 * Tranform common props to properties for use with a HTMLElement.
 */
export const applyCommonProps = <T extends object = object>(
  props: CommonProps & T,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any*/
  Component: React.FunctionComponent<any>,
  isAriaLabelBanned = false
) => {
  const componentName = Component.displayName || Component.name || 'Unknown';

  /* istanbul ignore if */
  if (process.env.NODE_ENV !== 'production') {
    /*
     * Consumers might not have TypeScript compilation enabled. The below checks
     * are to ensure `className` and `style` are not mistakenly passed in props.
     */

    // @ts-expect-error directive to explain why the @ts-expect-error is necessary. The description must be 3 characters or longer
    if (props.className) {
      /* eslint-disable-next-line no-console */
      console.error(
        `${componentName}.className property has been deprecated. Please use the "UNSAFE_className" property.`
      );
    }
    // @ts-expect-error directive to explain why the @ts-expect-error is necessary. The description must be 3 characters or longer
    if (props.style) {
      /* eslint-disable-next-line no-console */
      console.error(
        `${componentName}.style property has been deprecated. Please use the "UNSAFE_style" property.`
      );
    }
  }

  if (isAriaLabelBanned) {
    invariant(
      props['aria-label'] === undefined,
      `\`${componentName}\` accessibility violation. \`${componentName}\` is banned from using \`aria-label\`.`
    );
  }

  const appliedProps: AppliedCommonProps<Partial<T>> = {};

  for (const key in props) {
    // eslint-disable-next-line no-prototype-builtins
    if (props.hasOwnProperty(key)) {
      const value = props[key as keyof T];
      if (key === 'UNSAFE_className') {
        appliedProps.className = classNames(
          appliedProps.className,
          value as unknown as string
        );
      } else if (key === 'UNSAFE_style') {
        appliedProps.style = appliedProps.style
          ? {...appliedProps.style, ...value}
          : (value as unknown as React.CSSProperties);
      } else if (key !== 'className' && key !== 'style') {
        appliedProps[key as keyof T] = value;
      }
    }
  }
  return appliedProps as AppliedCommonProps<T>;
};
